import { SetAssetPracticeTask } from "../SetAssetPracticeTask.js";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { formatDate } from "../format.js";
import {
  addResourceFile,
  addResourceLink,
  archiveResource,
  attachResource,
  detachResource,
  getResourceTargets,
  getStaffResources,
  staffResourceOpenPath,
  updateResource,
  type ResourceTargetType,
  type ResourceTargets,
  type StudentResource,
} from "../resources.js";
import { ConfirmDialog } from "../ui/Dialog.js";
import { useAsync } from "../useAsync.js";

function formatBytes(value: number | null): string | null {
  if (value === null || !Number.isFinite(value) || value < 0) return null;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(value >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

function kindLabel(resource: StudentResource): string {
  if (resource.kind === "link") return "Web link";
  switch (resource.presentationKind) {
    case "pdf":
      return "PDF";
    case "image":
      return "Image";
    case "audio":
      return "Audio";
    case "video":
      return "Video";
    default:
      return "File";
  }
}

function targetLabel(type: ResourceTargetType): string {
  if (type === "practice_todo") return "Practice";
  if (type === "repertoire") return "Repertoire";
  return "Goal";
}

function targetsFor(targets: ResourceTargets | null, type: ResourceTargetType) {
  if (!targets) return [];
  if (type === "practice_todo") return targets.practiceTodos;
  if (type === "repertoire") return targets.repertoire;
  return targets.goals;
}

export function StudentResources({ studentId, onPracticeChanged }: { studentId: string; onPracticeChanged?: () => void }) {
  const [page, setPage] = useState(1);
  const resources = useAsync(() => getStaffResources(studentId, page), [studentId, page]);
  const targets = useAsync(() => getResourceTargets(studentId), [studentId]);
  const [createMode, setCreateMode] = useState<"file" | "link" | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [familyVisible, setFamilyVisible] = useState(true);
  const [editing, setEditing] = useState<StudentResource | null>(null);
  const [archiving, setArchiving] = useState<StudentResource | null>(null);
  const [attaching, setAttaching] = useState<StudentResource | null>(null);
  const [targetType, setTargetType] = useState<ResourceTargetType>("practice_todo");
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
    setCreateMode(null);
    setEditing(null);
    setArchiving(null);
    setAttaching(null);
    setProblem(null);
  }, [studentId]);

  const currentTargets = useMemo(
    () => targetsFor(targets.data ?? null, targetType),
    [targetType, targets.data],
  );

  function clearCreate() {
    setCreateMode(null);
    setTitle("");
    setDescription("");
    setUrl("");
    setFile(null);
    setFamilyVisible(true);
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!createMode || busy || title.trim() === "") return;
    if (createMode === "file" && !file) return;
    if (createMode === "link" && url.trim() === "") return;
    setBusy(true);
    setProblem(null);
    try {
      if (createMode === "file" && file) {
        await addResourceFile(studentId, {
          file,
          title,
          description: description || null,
          familyVisible,
        });
      } else {
        await addResourceLink(studentId, {
          title,
          description: description || null,
          url,
          familyVisible,
        });
      }
      clearCreate();
      setPage(1);
      resources.reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not create that resource.");
    } finally {
      setBusy(false);
    }
  }

  async function attachSelected() {
    if (!attaching || !targetId || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await attachResource(studentId, attaching.id, targetType, targetId);
      setAttaching(null);
      setTargetId("");
      resources.reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not attach that resource.");
    } finally {
      setBusy(false);
    }
  }

  async function detachExisting(
    resource: StudentResource,
    type: ResourceTargetType,
    id: string,
  ) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await detachResource(studentId, resource.id, type, id);
      resources.reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not detach that resource.");
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (!archiving || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await archiveResource(studentId, archiving.id);
      setArchiving(null);
      resources.reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not archive that resource.");
    } finally {
      setBusy(false);
    }
  }

  const data = resources.data;

  return (
    <section id="student-resources" aria-labelledby="student-resources-heading" style={{ scrollMarginTop: 16 }}>
      <div className="student-record-section-head">
        <div>
          <h2 id="student-resources-heading">Resources</h2>
          <p className="small muted">
            Share a file or web link once, then reuse it with practice work, repertoire or goals.
          </p>
        </div>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="quiet small" onClick={() => setCreateMode("link")}>
            Add link
          </button>
          <button type="button" className="small" onClick={() => setCreateMode("file")}>
            Upload file
          </button>
        </div>
      </div>

      {problem && <div className="error small" role="alert">{problem}</div>}
      {resources.error && <div className="error">{resources.error}</div>}

      {createMode && (
        <form className="panel stack" onSubmit={create} aria-label={createMode === "file" ? "Upload resource" : "Add resource link"}>
          <div className="spread">
            <h3 style={{ margin: 0 }}>{createMode === "file" ? "Upload resource" : "Add web link"}</h3>
            <button type="button" className="quiet" disabled={busy} onClick={clearCreate}>Close</button>
          </div>
          <label>
            <span className="small">Title</span>
            <input required maxLength={300} value={title} onChange={(event) => setTitle(event.target.value)} />
          </label>
          <label>
            <span className="small">Description (optional)</span>
            <textarea rows={2} maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} />
          </label>
          {createMode === "file" ? (
            <label>
              <span className="small">File</span>
              <input
                type="file"
                required
                accept="application/pdf,image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,audio/wav,audio/x-wav,audio/ogg,audio/webm,audio/flac,video/mp4,video/webm,video/quicktime"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              <span className="small muted" style={{ display: "block", marginTop: 4 }}>
                PDF, common images/audio, or supported video. Maximum 50 MB; the server verifies the actual file type.
              </span>
            </label>
          ) : (
            <label>
              <span className="small">Web address</span>
              <input type="url" required maxLength={2000} value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://…" />
            </label>
          )}
          <label className="row" style={{ justifyContent: "flex-start" }}>
            <input type="checkbox" checked={familyVisible} onChange={(event) => setFamilyVisible(event.target.checked)} />
            <span>Show this resource to the student/family</span>
          </label>
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="quiet" disabled={busy} onClick={clearCreate}>Cancel</button>
            <button
              type="submit"
              disabled={busy || title.trim() === "" || (createMode === "file" ? file === null : url.trim() === "")}
            >
              {busy ? "Saving…" : createMode === "file" ? "Upload" : "Add link"}
            </button>
          </div>
        </form>
      )}

      {editing && (
        <ResourceEditor
          studentId={studentId}
          resource={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            resources.reload();
          }}
        />
      )}

      {resources.loading && !data && <p className="empty">Loading resources…</p>}
      {data && data.items.length === 0 && (
        <div className="panel">
          <p className="empty">No reusable resources have been shared yet.</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="stack" style={{ gap: 10 }}>
          {data.items.map((resource) => {
            const size = formatBytes(resource.sizeBytes);
            const attachments = resource.attachments ?? [];
            return (
              <article className="panel" key={resource.id}>
                <div className="spread" style={{ gap: 16, alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong>{resource.title}</strong>
                    <div className="small muted" style={{ marginTop: 3 }}>
                      {kindLabel(resource)}
                      {size ? ` · ${size}` : ""}
                      {` · shared ${formatDate(resource.createdAt)}`}
                      {resource.familyVisible ? " · family visible" : " · staff only"}
                    </div>
                    {resource.description && <p className="notes" style={{ marginBottom: 6 }}>{resource.description}</p>}
                    {attachments.length > 0 && (
                      <div className="row small" style={{ flexWrap: "wrap", gap: 6, marginTop: 8 }} aria-label="Resource attachments">
                        {attachments.map((attachment) => (
                          <span className="tag" key={`${attachment.targetType}:${attachment.targetId}`}>
                            {targetLabel(attachment.targetType)}: {attachment.title}{" "}
                            <button
                              type="button"
                              className="link small"
                              disabled={busy}
                              aria-label={`Detach from ${attachment.title}`}
                              onClick={() => void detachExisting(resource, attachment.targetType, attachment.targetId)}
                            >
                              detach
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="row" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {resource.kind === "file" ? (
                      <a className="button quiet small" href={staffResourceOpenPath(studentId, resource.id)} target="_blank" rel="noopener noreferrer">Open</a>
                    ) : resource.externalUrl ? (
                      <a className="button quiet small" href={resource.externalUrl} target="_blank" rel="noopener noreferrer">Open</a>
                    ) : null}
                    <button
                      type="button"
                      className="quiet small"
                      onClick={() => {
                        setAttaching(resource);
                        setTargetType("practice_todo");
                        setTargetId("");
                      }}
                    >
                      Attach
                    </button>
                    {resource.familyVisible && <SetAssetPracticeTask target={{ domain: "private", learnerId: studentId }}
                      source={{ kind: "resource", id: resource.id }} assetTitle={resource.title} disabled={busy}
                      onSaved={() => { resources.reload(); targets.reload(); onPracticeChanged?.(); }} onBusyChange={setBusy} />}
                    <button type="button" className="quiet small" onClick={() => setEditing(resource)}>Edit</button>
                    <button type="button" className="quiet small" onClick={() => setArchiving(resource)}>Archive</button>
                  </div>
                </div>

                {attaching?.id === resource.id && (
                  <div className="row" style={{ marginTop: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <label>
                      <span className="small">Attach to</span>
                      <select
                        value={targetType}
                        onChange={(event) => {
                          setTargetType(event.target.value as ResourceTargetType);
                          setTargetId("");
                        }}
                      >
                        <option value="practice_todo">Practice task</option>
                        <option value="repertoire">Repertoire</option>
                        <option value="goal">Goal</option>
                      </select>
                    </label>
                    <label style={{ flex: "1 1 240px" }}>
                      <span className="small">Choose current item</span>
                      <select value={targetId} onChange={(event) => setTargetId(event.target.value)}>
                        <option value="">Choose…</option>
                        {currentTargets.map((target) => (
                          <option value={target.id} key={target.id}>{target.title}</option>
                        ))}
                      </select>
                    </label>
                    <button type="button" disabled={busy || !targetId} onClick={() => void attachSelected()}>
                      {busy ? "Attaching…" : "Attach resource"}
                    </button>
                    <button type="button" className="quiet" onClick={() => setAttaching(null)}>Cancel</button>
                    {targets.loading && <span className="small muted">Loading choices…</span>}
                    {targets.error && <span className="small error">Could not load attachment choices.</span>}
                    {!targets.loading && !targets.error && currentTargets.length === 0 && (
                      <span className="small muted">There are no current items of this type.</span>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {data && data.pages > 1 && (
        <div className="row" style={{ justifyContent: "space-between", marginTop: 12 }}>
          <button type="button" className="quiet" disabled={data.page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button>
          <span className="small muted">Page {data.page} of {data.pages}</span>
          <button type="button" className="quiet" disabled={data.page >= data.pages} onClick={() => setPage((value) => value + 1)}>Next</button>
        </div>
      )}

      {archiving && (
        <ConfirmDialog
          title="Archive this resource?"
          description="It will disappear from the active resource list, but existing teaching-history links and stored file bytes are retained."
          confirmLabel="Archive resource"
          confirmingLabel="Archiving…"
          busy={busy}
          onConfirm={archive}
          onClose={() => setArchiving(null)}
        >
          <p><strong>{archiving.title}</strong></p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function ResourceEditor({
  studentId,
  resource,
  onClose,
  onSaved,
}: {
  studentId: string;
  resource: StudentResource;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(resource.title);
  const [description, setDescription] = useState(resource.description ?? "");
  const [url, setUrl] = useState(resource.externalUrl ?? "");
  const [familyVisible, setFamilyVisible] = useState(resource.familyVisible);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || title.trim() === "") return;
    setBusy(true);
    setProblem(null);
    try {
      await updateResource(studentId, resource.id, {
        title,
        description: description || null,
        ...(resource.kind === "link" ? { url } : {}),
        familyVisible,
      });
      onSaved();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not update that resource.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel stack" onSubmit={submit} aria-label={`Edit ${resource.title}`}>
      <div className="spread">
        <h3 style={{ margin: 0 }}>Edit resource</h3>
        <button type="button" className="quiet" disabled={busy} onClick={onClose}>Close</button>
      </div>
      <label>
        <span className="small">Title</span>
        <input required maxLength={300} value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label>
        <span className="small">Description (optional)</span>
        <textarea rows={2} maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} />
      </label>
      {resource.kind === "link" && (
        <label>
          <span className="small">Web address</span>
          <input type="url" required maxLength={2000} value={url} onChange={(event) => setUrl(event.target.value)} />
        </label>
      )}
      <label className="row" style={{ justifyContent: "flex-start" }}>
        <input type="checkbox" checked={familyVisible} onChange={(event) => setFamilyVisible(event.target.checked)} />
        <span>Show this resource to the student/family</span>
      </label>
      {problem && <div className="error small" role="alert">{problem}</div>}
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <button type="button" className="quiet" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="submit" disabled={busy || title.trim() === "" || (resource.kind === "link" && url.trim() === "")}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}
